import moment from 'moment'
import React, { createContext, useContext, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { z } from 'zod'

import { localStorageInstance } from '@/modules/localStorage'
import { useQueryStateContext } from '@/ui/contexts/QueryStateContext'
import { useQueryState } from '@/ui/hooks/useQueryState'

import { ModulesFilter } from '../views/Activities/useActivitiesQuery'
import {
  SessionActivityStatusFilter,
  SessionStatusFilter
} from '../views/Session/useSessionQuery'

const tabEnum = z.enum(['SESSIONS', 'ACTIVITIES', 'CHARTS'])

const SessionsManagementContext = createContext<{
  tabParam: z.infer<typeof tabEnum> | undefined
  tabEnum: typeof tabEnum
  filter: {
    date: string
    startTime: string
    endTime: string
    personaName?: string | undefined
    status: SessionStatusFilter
    statusActivity: SessionActivityStatusFilter
    modules: ModulesFilter
    floor?: string | undefined
    onlyWithComments: boolean
    managementSpecialty: boolean
    onlyWithInterferingBehavior: boolean
    isPatientAbsence: boolean
  }
  settings: {
    reloadScreen: boolean
    pendingActivities: boolean
  }
  rawInputs: {
    personaName: string
    setPersonaName: React.Dispatch<React.SetStateAction<string>>
    startTime: string
    setStartTime: React.Dispatch<React.SetStateAction<string>>
    endTime: string
    setEndTime: React.Dispatch<React.SetStateAction<string>>
  }
  setTabParam: React.Dispatch<React.SetStateAction<z.infer<typeof tabEnum>>>
  setDate: React.Dispatch<React.SetStateAction<string>>
  setStatus: React.Dispatch<React.SetStateAction<SessionStatusFilter>>
  setStatusActivity: React.Dispatch<
    React.SetStateAction<SessionActivityStatusFilter>
  >
  setModules: React.Dispatch<React.SetStateAction<ModulesFilter>>
  setReloadScreen: React.Dispatch<React.SetStateAction<boolean>>
  setPendingActivities: React.Dispatch<React.SetStateAction<boolean>>
  setFloor: React.Dispatch<React.SetStateAction<string | undefined>>
  setOnlyWithComments: React.Dispatch<React.SetStateAction<boolean>>
  setOnlyWithInterferingBehavior: React.Dispatch<React.SetStateAction<boolean>>
  setManagementSpecialty: React.Dispatch<React.SetStateAction<boolean>>
  setIsPatientAbsence: React.Dispatch<React.SetStateAction<boolean>>
}>({
  tabParam: 'SESSIONS',
  tabEnum,
  filter: {
    date: '',
    startTime: '',
    endTime: '',
    personaName: undefined,
    status: 'NONE',
    statusActivity: 'NONE',
    modules: 'NONE',
    floor: undefined,
    onlyWithComments: false,
    onlyWithInterferingBehavior: false,
    managementSpecialty: false,
    isPatientAbsence: false
  },
  setTabParam: () => {},
  settings: {
    reloadScreen: false,
    pendingActivities: false
  },
  rawInputs: {
    personaName: '',
    setPersonaName: () => {},
    startTime: '',
    setStartTime: () => {},
    endTime: '',
    setEndTime: () => {}
  },
  setDate: () => {},
  setStatus: () => {},
  setStatusActivity: () => {},
  setModules: () => {},
  setReloadScreen: () => {},
  setOnlyWithComments: () => {},
  setOnlyWithInterferingBehavior: () => {},
  setManagementSpecialty: () => {},
  setPendingActivities: () => {},
  setFloor: () => {},
  setIsPatientAbsence: () => {}
})

export const useSessionsManagementContext = () =>
  useContext(SessionsManagementContext)

const DEBOUNCE_DELAY = 500

export default function SessionsManagementProvider({
  children
}: {
  children: React.ReactNode
}) {
  const [tabParam, setTabParam] = useQueryState<z.infer<typeof tabEnum>>(
    'TAB',
    tabEnum.enum.SESSIONS
  )

  const [searchParams] = useQueryStateContext()

  const persistentFilters = localStorageInstance.getSessionManagementFilter()

  const filter = {
    personaName: searchParams.get('personaName') ?? '',
    startTime:
      searchParams.get('startTime') || moment().startOf('hour').format('HH:00'),
    endTime:
      searchParams.get('endTime') ||
      moment().startOf('hour').add(1, 'hour').format('HH:00'),
    date: searchParams.get('date'),
    status:
      (searchParams.get('status') as SessionStatusFilter | null) ?? 'NONE',
    statusActivity:
      (searchParams.get(
        'statusActivity'
      ) as SessionActivityStatusFilter | null) ?? 'NONE',
    modules: (searchParams.get('modules') as ModulesFilter | null) ?? 'NONE',
    pendingActivities:
      persistentFilters?.pendingActivities ??
      searchParams.get('pendingActivities') === 'true',
    reloadScreen: persistentFilters?.reloadScreen,
    floor: persistentFilters?.floor ?? searchParams.get('floor'),
    onlyWithComments: searchParams.get('onlyWithComments') === 'true',
    isPatientAbsence: searchParams.get('isPatientAbsence') === 'true',
    onlyWithInterferingBehavior:
      searchParams.get('onlyWithInterferingBehavior') === 'true',
    managementSpecialty:
      persistentFilters?.managementSpecialty ??
      searchParams.get('managementSpecialty') === 'true'
  }

  const [date, setDate] = useState(
    moment(filter.date ?? undefined).format(moment.HTML5_FMT.DATE)
  )

  const [rawStartTime, setRawStartTime] = useState<string>(filter.startTime)
  const [debouncedStartTime] = useDebounceValue(rawStartTime, DEBOUNCE_DELAY)

  const [rawEndTime, setRawEndTime] = useState<string>(filter.endTime)
  const [debouncedEndTime] = useDebounceValue(rawEndTime, DEBOUNCE_DELAY)

  const [rawPersonaName, setRawPersonaName] = useState<string>(
    filter.personaName
  )

  const [debouncedPersonaName] = useDebounceValue(
    rawPersonaName,
    DEBOUNCE_DELAY
  )

  const [status, setStatus] = useState<SessionStatusFilter>(filter.status)

  const [statusActivity, setStatusActivity] =
    useState<SessionActivityStatusFilter>(filter.statusActivity ?? 'NONE')

  const [modules, setModules] = useState<ModulesFilter>(filter.modules)

  const [reloadScreen, setReloadScreen] = useState<boolean>(
    filter.reloadScreen ?? false
  )
  const [pendingActivities, setPendingActivities] = useState<boolean>(
    filter.pendingActivities ?? false
  )
  const [floor, setFloor] = useState<string | undefined>(
    filter.floor ?? undefined
  )

  const [onlyWithComments, setOnlyWithComments] = useState<boolean>(
    filter.onlyWithComments
  )

  const [onlyWithInterferingBehavior, setOnlyWithInterferingBehavior] =
    useState<boolean>(filter.onlyWithInterferingBehavior)

  const [managementSpecialty, setManagementSpecialty] = useState<boolean>(
    filter.managementSpecialty
  )
  const [isPatientAbsence, setIsPatientAbsence] = useState<boolean>(
    filter.isPatientAbsence
  )

  return (
    <SessionsManagementContext.Provider
      value={{
        tabParam,
        tabEnum,
        filter: {
          date,
          startTime: debouncedStartTime,
          endTime: debouncedEndTime,
          personaName: debouncedPersonaName,
          status,
          statusActivity,
          modules,
          floor,
          onlyWithComments,
          onlyWithInterferingBehavior,
          managementSpecialty,
          isPatientAbsence
        },
        rawInputs: {
          personaName: rawPersonaName,
          setPersonaName: setRawPersonaName,
          startTime: rawStartTime,
          setStartTime: setRawStartTime,
          endTime: rawEndTime,
          setEndTime: setRawEndTime
        },
        settings: {
          reloadScreen,
          pendingActivities
        },
        setTabParam,
        setDate,
        setStatus,
        setStatusActivity,
        setModules,
        setReloadScreen,
        setPendingActivities,
        setFloor,
        setOnlyWithComments,
        setOnlyWithInterferingBehavior,
        setManagementSpecialty,
        setIsPatientAbsence
      }}
    >
      {children}
    </SessionsManagementContext.Provider>
  )
}
